import { makeAutoObservable } from 'mobx'
import { injectable, inject, decorate } from '@evertel/di'
import { DocumentSchemaStore } from '../../store'
import { APIDataDocumentSchema } from '@evertel/types'
import { Api } from '@evertel/api'


class DepartmentDocumentSchemasController {
    departmentId = 0

    constructor(
        private documentSchemaStore: DocumentSchemaStore,
        private api: Api
    ) {
        makeAutoObservable(this)
    }

    init = async (departmentId: number) => {
        this.departmentId = departmentId

        await this.fetchDepartmentDocumentSchemas()
    }

    fetchDepartmentDocumentSchemas = async (): Promise<APIDataDocumentSchema[]> => {
        if (!this.departmentId) return []

        const schemas = await this.api.Routes.Department.getDocumentSchemas(this.departmentId, {
            order: 'name ASC',
            include: ['categories', 'iconMedia']
        })

        if (schemas?.length) this.documentSchemaStore.update(schemas)

        return schemas
    }

    setSchemaEnabled = async (schemaId: number, isEnabled: boolean): Promise<APIDataDocumentSchema> => {
        const schema = await this.api.Routes.DocumentSchema.putById(schemaId, { isEnabled })

        // categories are not included in the put response, keep the ones already in the store
        const stored = this.documentSchemaStore.findById(schemaId)
        this.documentSchemaStore.update([{
            ...stored,
            ...schema,
            categories: stored?.categories ?? schema.categories
        }])

        return schema
    }

    enableSchema = (schemaId: number) => this.setSchemaEnabled(schemaId, true)

    disableSchema = (schemaId: number) => this.setSchemaEnabled(schemaId, false)

    get documentSchemaIds() {
        return this.documentSchemaStore.find(d => d.departmentId === this.departmentId).map(d => d.id)
    }

    // the store returns insertion order, so the API's `name ASC` doesn't survive; sort here
    // so every derived list (enabled/disabled/per-category) is alphabetical
    get documentSchemas() {
        return this.documentSchemaStore.findByIds(this.documentSchemaIds)
            .slice()
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }))
    }

    get enabledDocumentSchemas() {
        return this.documentSchemas.filter(d => d.isEnabled === true)
    }

    get disabledDocumentSchemas() {
        return this.documentSchemas.filter(d => d.isEnabled === false)
    }

    get uncategorizedDocumentSchemas() {
        return this.enabledDocumentSchemas.filter(d => !d.categories || d.categories.length === 0)
    }

    schemasForCategory(categoryId: number): APIDataDocumentSchema[] {
        return this.enabledDocumentSchemas.filter(s =>
            s.categories?.some(c => c.id === categoryId)
        )
    }
}

decorate(injectable(), DepartmentDocumentSchemasController)
decorate(inject(DocumentSchemaStore), DepartmentDocumentSchemasController, 0)
decorate(inject(Api), DepartmentDocumentSchemasController, 1)

export { DepartmentDocumentSchemasController }
